import {
  IPrivateChat,
  IGroupChat,
  ITextMessage,
  IDataMessage,
} from 'Shared/Types/chat';
import { getChatsList, getChatMessages } from './reducer';
import { RootState } from 'Redux/store';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

interface IChatState {
  list: (IPrivateChat | IGroupChat)[];
  filteredList: (IPrivateChat | IGroupChat)[] | null;
  messages: (ITextMessage | IDataMessage)[];
  error?: string | null;
  loading: boolean;
}

const getTimestampInMs = (timestamp: string | number | null): number => {
  if (timestamp === null) {
    return 0;
  }

  const timestampInMs = new Date(timestamp).getTime();

  return timestampInMs ? timestampInMs : 0;
};

const initialState: IChatState = {
  list: [],
  filteredList: null,
  messages: [],
  error: null,
  loading: false,
};

const chatSlice = createSlice({
  name: 'chat',
  initialState,
  reducers: {
    resetChatMessages: (state) => {
      state.messages = [];
    },
    resetChatsList: (state) => {
      state.list = [];
    },
    addChat: (state, action: PayloadAction<IPrivateChat | IGroupChat>) => {
      const newChat = action.payload;
      newChat.last_timestamp = getTimestampInMs(newChat.last_timestamp);
      const updatedList = [newChat, ...state.list];

      if (updatedList.length > 2) {
        updatedList.sort(
          (a, b) => Number(b.last_timestamp) - Number(a.last_timestamp),
        );
      }

      state.list = updatedList;
    },
    addChatMessage: (
      state,
      action: PayloadAction<ITextMessage | IDataMessage>,
    ) => {
      state.messages = [...state.messages, action.payload];
    },
    filterChatsList: (state, action: PayloadAction<string>) => {
      const searchTerm = action.payload.toLowerCase();

      if (searchTerm === '') {
        state.filteredList = null;
        return;
      }

      const actualList =
        state.filteredList !== null && state.filteredList.length > 1
          ? state.filteredList
          : state.list;

      state.filteredList = actualList.filter((chat) =>
        chat.name.toLowerCase().includes(searchTerm),
      );
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getChatsList.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(getChatsList.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message;
    });
    builder.addCase(getChatsList.fulfilled, (state, action) => {
      state.loading = false;
      state.error = null;
      let result: (IPrivateChat | IGroupChat)[] = action.payload;
      if (result.length) {
        result = result.map((item) => {
          return {
            ...item,
            last_timestamp: getTimestampInMs(item.last_timestamp),
          };
        });
      }
      if (result.length > 2) {
        result.sort(
          (a, b) => Number(b.last_timestamp) - Number(a.last_timestamp),
        );
      }
      state.list = result;
    });

    builder.addCase(getChatMessages.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(getChatMessages.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message;
    });
    builder.addCase(getChatMessages.fulfilled, (state, action) => {
      state.loading = false;
      state.error = null;
      state.messages = action.payload || [];
    });
  },
});

export const { resetChatMessages, resetChatsList, addChatMessage, filterChatsList } =
  chatSlice.actions;

export const selectChatLoading = (state: RootState) => state.chat.loading;
export const selectChatError = (state: RootState) => state.chat.error;
export const selectChatsList = (state: RootState) =>
  state.chat.filteredList !== null ? state.chat.filteredList : state.chat.list;
export const selectChatMessages = (state: RootState) => state.chat.messages;

export default chatSlice;
